// UserInfoList.js
import React, { useState } from 'react';
import { Box } from '@mui/system';
import { Button } from '@mui/material';
import {useReactiveVar} from '@apollo/client';
import {userInfoRepo} from '../../model/CvoModel';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import WavingHandIcon from '@mui/icons-material/WavingHand'; // 해지.
import LockPersonIcon from '@mui/icons-material/LockPerson'; // 일시정지.
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore'; // 복원.
import FlawedList from '../message/FlawedList';
import FlexyTable from '../common/flexytable/FlexyTable';
import ValueUtil from '../../model/ValueUtil';
import { AppPalette, AppWord, TableColumnType } from '../../model/AppConst';
import styled from '@emotion/styled';
import { PersonAdd } from '@mui/icons-material';
import { ContentHeader, HeaderLeft, HeaderRight } from '../common/CvoStyled';
import SearchBox from '../common/SearchBox';
import { UserInfoDictionary } from './UserInfoGql';
const LocalTitle = '사용자정보';

const MainListBox = styled(Box)({height:'100%', display:'flex', flexDirection:'column'});

const TableColumns = [
	{
        id: 'userId', label: '아이디', width: 100, show: true,
        option:{
            align: 'inherit', type: TableColumnType.TEXT,
            print: (r, i, v) => {
                if(r.userStateCd === UserInfoDictionary.userState.LEFT) {
                    return <span style={{color: '#999'}}>{v}</span>;
                }
                else if(r.userStateCd === UserInfoDictionary.userState.STOPPED) {
                    return <span style={{color: AppPalette.WarnRGB}}>{v}</span>;
                }
                return <span>{v}</span>;
            }
        }
    },
    { id: 'userName', label: '이름', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'userStateValue', label: '상태', width: 80, show: true, option:{align: 'center', type: TableColumnType.TEXT} },
    { id: 'className', label: '권한', width: 100, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'loginTime', label: '로그인', width: 180, show: true, option:{align: 'inherit', type: TableColumnType.DATETIME} },
    { id: 'mobile', label: '휴대전화', width: 110, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'telno', label: '연락처', width: 110, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
    { id: 'email', label: '메일', width: 200, show: true, option:{align: 'inherit', type: TableColumnType.TEXT} },
];

export default function UserInfoList({
	maxHeight,
	maxWidth,
	selected,
    onClickItem,
    onRequestAdd,
    onRequestEdit,
    onRequestRemove,
    onRequestAsIs,
    onRequestView,
    responseList,
    onRequestReload,
    onRequestSetState,
}) {
    const [search, setSearch] = useState('');
    const sessionInfo = useReactiveVar(userInfoRepo);
    const {loading:loadingList, error:errorOfList, data:dataList} = responseList;



    const onChangeSearch = (text) => {
        setSearch(text);
    };

    const onClickToolSetState = (row, newState) => {
        if(onRequestSetState) {
            const param = {
                userId: row.userId,
                custId: row.custId,
                userStateCd: newState
            };
            onRequestSetState(param);
        }
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppWord.CMD_ASIS: if(onRequestAsIs) onRequestAsIs(row); break;
            case AppWord.CMD_REMOVE: onRequestRemove(row); break;
            case AppWord.CMD_EDIT: onRequestEdit(row); break;
            case AppWord.CMD_VIEW:
                if(onRequestView) onRequestView(row);
                break;
            case AppWord.CMD_C_STOP:
                onClickToolSetState(row, UserInfoDictionary.userState.STOPPED);
                break;
            case AppWord.CMD_C_LEFT:
                onClickToolSetState(row, UserInfoDictionary.userState.LEFT);
                break;
            case AppWord.CMD_C_RECOVER:
                onClickToolSetState(row, UserInfoDictionary.userState.NORMAL);
                break;
            default: return;
        }
    };

    const onSelectItem = (item) => {
		if(onClickItem) onClickItem(item);
    };


    // Handling list event
    if(ValueUtil.flawedListResponse(responseList))
        return <FlawedList title={LocalTitle} gqlResponse={responseList} />;

    const Tools = [
        {id:AppWord.CMD_VIEW, icon:<ContactPageIcon fontSize={AppWord.SMALL} />, title: '상세보기'},
        {id:AppWord.CMD_EDIT, icon:<NoteAltIcon fontSize={AppWord.SMALL} />, title: '수정'},
        {id:AppWord.CMD_REMOVE, icon: <DeleteForeverIcon fontSize={AppWord.SMALL} />, title: '사용자 삭제'}
    ];

    if(sessionInfo.isCustAdmin() || sessionInfo.isEtrace()) {
        Tools.push({
            id: AppWord.CMD_C_STOP,
            icon: <LockPersonIcon fontSize={AppWord.SMALL} color={AppPalette.InheritColor} />,
            title: '일시정지',
            where: (row) => {
                return row.userStateCd === UserInfoDictionary.userState.NORMAL;
            }
        });
        Tools.push({
            id: AppWord.CMD_C_LEFT,
            icon: <WavingHandIcon fontSize={AppWord.SMALL} color={AppPalette.WarnColor} />,
            title: '해지',
            where: (row) => {
                return row.userStateCd !== UserInfoDictionary.userState.LEFT;
            }
        });
        Tools.push({
            id: AppWord.CMD_C_RECOVER,
            icon: <SettingsBackupRestoreIcon fontSize={AppWord.SMALL} color={AppPalette.PrimaryColor} />,
            title: '사용자 복원',
            where: (row) => {
                return row.userStateCd !== UserInfoDictionary.userState.NORMAL;
            }
        });
    }

    if(sessionInfo.user.etrace) {
        Tools.push({
            id:AppWord.CMD_ASIS,
            icon:<VerifiedUserIcon fontSize={AppWord.SMALL} color='secondary' />,
            title: '다른 사용자인 척 가장하기'
        });
    }

    return(
        <MainListBox>
            <ContentHeader>
                <HeaderLeft>
                    <SearchBox onChange={onChangeSearch} value={search} width={160}/>
                </HeaderLeft>
                <HeaderRight>
                    {
                        sessionInfo.isCustAdmin() || sessionInfo.isEtrace() ?
                        <Button
                            onClick={onRequestAdd}
                            variant='contained' color={AppPalette.PrimaryColor}
                            startIcon={<PersonAdd fontSize={AppWord.SMALL} />}
                            disabled={loadingList}
                        >
                            사용자 추가
                        </Button>
                        : null
                    }
                </HeaderRight>
            </ContentHeader>
            <FlexyTable
                name="user_info_list"
                uniqueKey="userId"
                columns={TableColumns}
                records={dataList.userInfoList}
                search={search}
                tools={Tools}
                onClickTool={onClickToolOnRow}
				onClickOneRow={onSelectItem}
				selected={selected ? [selected] : null}
                onRequestReload={onRequestReload}
                defaultSorter={{id:'userName', type: TableColumnType.TEXT}}
            />
        </MainListBox>
    );

}
